/**
 * Chutt... - Share of Voice Chart (Style Meltwater)
 * Barre horizontale empilee montrant la repartition par plateforme.
 * Legende en dessous avec pastilles colorees + nom + count + pourcentage.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { colors, meltwaterPalette } from '../../styles/colors';

const PLATFORM_LABELS = {
  twitter: 'Twitter/X',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  news: 'Actualites',
  forum: 'Forums',
  web: 'Web',
  youtube: 'YouTube',
  google_reviews: 'Google',
};

const PLATFORM_COLORS = {
  twitter: '#1DA1F2',
  facebook: '#4267B2',
  linkedin: '#0077B5',
  instagram: '#E4405F',
  news: '#8B5CF6',
  forum: '#FF6B35',
  web: '#94A3B8',
  youtube: '#FF0000',
  google_reviews: '#EA4335',
};

function ShareOfVoiceChart({ sources }) {
  if (!sources || sources.length === 0) return null;

  // Assign colors to sources
  const coloredSources = sources.map((s, i) => ({
    ...s,
    color: s.color || PLATFORM_COLORS[s.platform] || meltwaterPalette.platforms[i % meltwaterPalette.platforms.length],
    label: PLATFORM_LABELS[s.platform] || s.platform || 'Autre',
  }));

  // Build a single data point with each platform as a separate key
  const barData = [{}];
  coloredSources.forEach((s) => {
    barData[0][s.platform] = s.count;
  });

  const total = coloredSources.reduce((sum, s) => sum + (s.count || 0), 0);

  return (
    <Box>
      {/* Horizontal stacked bar */}
      <ResponsiveContainer width="100%" height={56}>
        <BarChart
          data={barData}
          layout="vertical"
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          barCategoryGap={0}
        >
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" hide />
          {coloredSources.map((s) => (
            <Bar
              key={s.platform}
              dataKey={s.platform}
              stackId="sov"
              fill={s.color}
              radius={0}
              animationBegin={0}
              animationDuration={800}
              animationEasing="ease-out"
            />
          ))}
          <RechartsTooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              return (
                <Box sx={{
                  bgcolor: colors.charcoal,
                  color: 'white',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                }}>
                  {payload.map((p) => {
                    const src = coloredSources.find(s => s.platform === p.dataKey);
                    return (
                      <Box key={p.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: src?.color }} />
                        <Typography sx={{ fontSize: '0.75rem', color: 'white' }}>
                          {src?.label}: {p.value} ({total > 0 ? Math.round((p.value / total) * 100) : 0}%)
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              );
            }}
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Legend with colored dots */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
        {coloredSources.map((s) => {
          const pct = total > 0 ? Math.round((s.count / total) * 100) : 0;
          return (
            <Box key={s.platform} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.8rem', color: colors.gray600, fontWeight: 500 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: colors.charcoal, fontWeight: 700 }}>
                {s.count}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: colors.gray400 }}>
                ({pct}%)
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default ShareOfVoiceChart;
